import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '@/components/Layout/Sidebar';
import TopBar from '@/components/Layout/TopBar';
import { TopBarSlotContext } from '@/components/Layout/TopBarSlot';
import UIContextBridge from '@/components/SAM/UIContextBridge';
import { cn } from '@/utils/cn';
import { useIsDesktop } from '@/hooks/useMediaQuery';

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(() =>
    localStorage.getItem('desktopSidebarHidden') === 'true'
  );
  // The TopBar exposes a slot element; pages (e.g. AI Chat) portal controls into it.
  const [topBarSlot, setTopBarSlot] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();
  // The AI Chat owns its full surface (dark, edge-to-edge), so it opts out of the
  // standard gray page padding that every other module page uses.
  const fullBleed = pathname === '/chat';

  const isDesktopViewport = useIsDesktop();
  // The mobile drawer cannot stay open once the viewport grows to the desktop shell. Adjusting state during
  // render (rather than in an effect) avoids a frame where the drawer and the static sidebar are both visible.
  if (isDesktopViewport && sidebarOpen) setSidebarOpen(false);

  const handleMenuClick = () => {
    if (isDesktopViewport) {
      const next = !desktopSidebarHidden;
      setDesktopSidebarHidden(next);
      localStorage.setItem('desktopSidebarHidden', String(next));
    } else {
      setSidebarOpen(true);
    }
  };

  return (
    <div className="flex h-dvh bg-ui font-poppins">
      {/* Always-mounted situational-awareness bridge (lives here, not in ChatPage, so SAM can
          read the current page/mode/view from anywhere — incl. the future dockable chat). */}
      <UIContextBridge />

      {/* Mobile/tablet backdrop — tap outside to close */}
      {sidebarOpen && (
        <div
          className="modal-scrim fixed inset-0 z-drawer lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — off-canvas drawer on mobile/tablet, static column on lg+ */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-drawer transition-all duration-300 ease-in-out',
          // viewport-fit=cover lets the drawer run under the notch / home indicator on phones.
          'bg-chrome-from pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] lg:bg-transparent lg:p-0',
          'lg:relative lg:translate-x-0 lg:shadow-none',
          // Closed drawers are `invisible`: no shadow bleeding onto the screen edge, and their links
          // stop being reachable by keyboard while off-canvas. The shadow only exists while open.
          sidebarOpen ? 'visible translate-x-0 shadow-pop' : 'invisible -translate-x-full',
          desktopSidebarHidden ? 'lg:invisible lg:w-0 lg:overflow-hidden lg:opacity-0' : 'lg:visible'
        )}
      >
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Page content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar onMenuClick={handleMenuClick} dark={fullBleed} slotRef={setTopBarSlot} />
        <TopBarSlotContext.Provider value={topBarSlot}>
          <main className={cn('flex-1 overflow-y-auto', !fullBleed && 'p-4 md:p-6')}>
            <Outlet />
          </main>
        </TopBarSlotContext.Provider>
      </div>
    </div>
  );
};

export default DashboardLayout;
